import { useState } from "react";
import { Link } from "react-router-dom";
import { NAV_LINKS, CALENDLY_URL } from "../lib/site";
import { ArrowUpRight, Settings, Mail, Phone, MapPin, Instagram } from "lucide-react";
import ManageSubscriptionModal from "./ManageSubscriptionModal";

export default function Footer() {
  const [manageOpen, setManageOpen] = useState(false);
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#1A1A1A] text-white border-t-4 border-[#0055FF]"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#FFD700]">
              TinkerShift
            </p>
            <p className="mt-3 font-display font-black text-3xl md:text-4xl leading-[1.05] tracking-tight">
              Future-ready learning for schools, libraries and communities.
            </p>
            <p className="mt-5 text-sm md:text-base text-white/70 max-w-md">
              Powered by TinkerTank — 10+ years of hands-on creative technology, STEM and
              maker learning across schools, libraries and councils.
            </p>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-book-call-btn"
              className="mt-6 inline-flex items-center gap-2 bg-[#FFD700] text-[#1A1A1A] border-2 border-[#FFD700] rounded-full px-6 py-3 font-bold uppercase text-sm tracking-wide nb-press"
            >
              Book a Discovery Call <ArrowUpRight size={16} strokeWidth={2.5} />
            </a>
          </div>

          <div className="md:col-span-4 grid grid-cols-2 gap-6 text-sm">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-white/50 mb-3">
                Navigate
              </p>
              <ul className="space-y-2">
                {NAV_LINKS.slice(0, 4).map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="font-semibold hover:text-[#00C2B8]"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-white/50 mb-3">
                Company
              </p>
              <ul className="space-y-2">
                {NAV_LINKS.slice(4).map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="font-semibold hover:text-[#FF007F]"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="md:col-span-3 text-sm">
            <p className="font-mono text-xs uppercase tracking-widest text-white/50 mb-3">
              Get in touch
            </p>
            <ul className="space-y-2.5">
              <li>
                <a
                  href="mailto:hello@tinkertank.rocks"
                  data-testid="footer-contact-email"
                  className="group flex items-center gap-2.5 text-white/85 hover:text-[#FFD700] transition-colors"
                >
                  <Mail size={14} strokeWidth={2.5} className="opacity-70 group-hover:opacity-100" />
                  hello@tinkertank.rocks
                </a>
              </li>
              <li>
                <a
                  href="tel:+61455400261"
                  data-testid="footer-contact-phone"
                  className="group flex items-center gap-2.5 text-white/85 hover:text-[#00C2B8] transition-colors"
                >
                  <Phone size={14} strokeWidth={2.5} className="opacity-70 group-hover:opacity-100" />
                  0455 400 261
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-white/70">
                <MapPin size={14} strokeWidth={2.5} className="mt-0.5 opacity-70 flex-shrink-0" />
                <span data-testid="footer-contact-address">
                  50 Yeo Street
                  <br />
                  Neutral Bay NSW
                </span>
              </li>
              <li>
                <a
                  href="https://instagram.com/_tinkertank_"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-contact-instagram"
                  className="group inline-flex items-center gap-2.5 text-white/85 hover:text-[#FF007F] transition-colors"
                >
                  <Instagram size={14} strokeWidth={2.5} className="opacity-70 group-hover:opacity-100" />
                  @_tinkertank_
                </a>
              </li>
            </ul>
            <button
              type="button"
              onClick={() => setManageOpen(true)}
              data-testid="footer-manage-subscription-btn"
              className="mt-5 inline-flex items-center gap-2 bg-transparent text-white/90 border border-white/30 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-[#1A1A1A] transition-colors"
            >
              <Settings size={13} strokeWidth={2.5} />
              Manage subscription
            </button>
            <div className="mt-5 flex gap-2">
              <span className="h-3 w-10 rounded-full bg-[#00C2B8]" />
              <span className="h-3 w-10 rounded-full bg-[#FFD700]" />
              <span className="h-3 w-10 rounded-full bg-[#FF007F]" />
              <span className="h-3 w-10 rounded-full bg-[#FF6600]" />
              <span className="h-3 w-10 rounded-full bg-[#0055FF]" />
            </div>
          </div>
        </div>

        <div className="mt-14 border-t border-white/15 pt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <p className="font-display font-black text-[14vw] md:text-[10vw] leading-[0.85] tracking-tighter">
            LEARN<span className="text-[#FFD700]">.</span>SHIFT<span className="text-[#FF007F]">.</span>
          </p>
          <p className="text-xs text-white/50 font-mono">
            © {new Date().getFullYear()} TinkerShift · Powered by TinkerTank
          </p>
        </div>
      </div>
      <ManageSubscriptionModal open={manageOpen} onOpenChange={setManageOpen} />
    </footer>
  );
}
